import { SubjectCategoryEnum } from 'src/constants/subjectCategory';

import { User } from './user';
import { PaginatedResponse } from '../types/common';
import { httpClient, QueryParams } from './httpClient';
import { AssessmentDimension } from './assessmentDimension';

export enum ProjectType {
  BusinessPlan = 'BusinessPlan',
}

export enum TypeOfChange {
  Income = 'Income',
  Expense = 'Expense',
}

export enum CalculationMethod {
  Auto = 'Auto',
  ManualInput = 'ManualInput',
}

export enum MeasurementUnit {
  Percentage = '%',
  Currency = '￥',
  Count = '个/件/位',
}

export enum TypeCode {
  HE = 'HE 人效 Human Efficiency',
  MPR = 'MPR 边界利润率 Marginal Profit Rate',
  TRAR = 'TRAR 招聘及时到岗率 Timely Recruitment Arrival Rate',
  PCR = 'PCR 回款率 Payment Collection Rate',
  FSAR = 'FSAR 一版通过率 First Sample Approval Rate',
  ODR = 'ODR 准交率 On-time Delivery Rate',
  AR = 'AR 准确率 Accuracy Rate',
  PCA = 'PCA 回款额 Payment Collection Amount',
  LCR = 'LCR 人工成本占比 Labor Cost Ratio',
  CRR = 'CRR 成本下调率 Cost Reduction Rate',
  ISPGM = 'ISPGM 车缝内加工毛利率 Internal Sewing Processing Gross Margin',
  ESPGM = 'ESPGM 车缝外加工毛利率 External Sewing Processing Gross Margin',
  GMR = 'GMR 毛利率 Gross Margin Rate',
  OI = 'OI 接单额 Order Intake',
  OP = 'OP 经营利润 Operating Profit',
  IOR = 'IOR 投入-产出比(Q) Input-Output Ratio (Q)',
  BFODR = 'BFODR 大货面料准交率 Bulk Fabric On-time Delivery Rate',
  FPRR = 'FPRR 运费价格下降率 Freight Price Reduction Rate',
  IEPRR = 'IEPRR 国际快递价下降率 International Express Price Reduction Rate',
  HESD = 'HESD 人效(船务文件) Human Efficiency (Shipping Documents)',
  DAR = 'DAR 文件准确率 Document Accuracy Rate',
  SOTDR = 'SOTDR 货物准交率 Shipment On-Time Delivery Rate',
  SAL = 'SAL 出货额 Sales',
  OHE = 'OHE 总人效 Overall Human Efficiency',
  NOI = 'NOI 询盘数 Number of Inquiries',
  CR = 'CR 转化率 Conversion Rate',
  SODR = 'SODR 样版时效准交率 Sample On-time Delivery Rate',
  BFPR = 'BFPR 大货面料合格率 Bulk Fabric Pass Rate',
  CCO = 'CCO 客诉(单) Customer Complaints (Orders)',
  BOPR = 'BOPR 大货合格率 Bulk Order Pass Rate',
  TQ = 'TQ 任务数量 Task Quantity',
  BQ = 'BQ 修复数量 Bug Quantity',
  IQ = 'IQ 更新迭代数量 Iteration Quantity',
  TS = 'TS 总销售额 Total Sales',
  OV = 'OV 产值 Output Value',
  PC = 'PC 件数 Piece Count',
  ETR = 'ETR 人员流失率 Employee Turnover Rate',
  OODR = 'OODR 订单准交率 Order On-time Delivery Rate',
  OPR = 'OPR 订单合格率 Order Pass Rate',
  MCR = 'MCR 月结占比 Monthly Credit Ratio',
  CFC = 'CFC 现金流管控 Cash Flow Control',
  BAR = 'BAR 预算准确率 Budget Accuracy Rate',
  OT = 'OT 其他 other',
  EIB = '期末库存结余 Ending inventory balance',
  SR = '销售额 Sales revenue',
  NS = '销售净额 Net sales',
  COP = '产品成本 Product cost',
  GP = '毛利 Gross profit',
  VE = '变动费用 Variable expenses',
  CM = '边界利润 Contribution margin',
  FC = '固定费用 Fixed charge',
  OEA = '经营费用分摊 Operating expenses allocation',
  NOPI = '投入人员数 Number of personnel involved',
  OPPE = '人均经营利润 Operating profit per employee',
  BEPS = '盈亏平衡点销售额 Break-even point sales',
}

export enum VarianceAnalysisType {
  KPI = 'KPI', // 业绩分析表
  KPI_SUM = 'KPI_SUM', // 1~N月累计业绩分析表
  DEPARTMENTS_KPI = 'DEPARTMENTS_KPI', // 各部门业绩分析表
  KPI_TREND = 'KPI_TREND', // 业绩趋势分析表
}

export interface VarianceAnalysisSetting {
  type?: VarianceAnalysisType;
  frequencyType?: string;
}

export interface Subject {
  id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  description?: string;
  comment?: string;
  projectType?: ProjectType;
  typeOfProject?: string;
  parentId?: string;
  isSystemValue?: boolean;
  typeOfChange?: TypeOfChange;
  calculationMethod?: CalculationMethod;
  calculationFormula?: string;
  amoebaCalculationFormula?: string;
  companyCalculationFormula?: string;
  measurementUnit?: string;
  creator?: User;
  lastUpdater?: User;
  assessmentDimension?: AssessmentDimension;
  typeCode?: keyof typeof TypeCode;
  type?: string;
  enabled?: boolean;
  code?: string;
  varianceAnalysisSettings?: VarianceAnalysisSetting[];
  category?: SubjectCategoryEnum;
}

export interface GetAllSubjectsParams {
  page?: number;
  size?: number;
}

export async function getAllSubjects(
  params?: GetAllSubjectsParams
): Promise<PaginatedResponse<Subject>> {
  const queryParams: QueryParams = {};

  if (params?.page !== undefined) {
    queryParams.page = params.page.toString();
  }

  if (params?.size !== undefined) {
    queryParams.size = params.size.toString();
  }

  const response = await httpClient.sendRequest({
    url: `subjects`,
    method: 'GET',
    queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
  });

  return {
    items: response.data,
    total: response.data.length,
    page: params?.page || 1,
    size: params?.size || response.data.length,
  };
}

export async function getSubject(id: string): Promise<Subject> {
  const response = await httpClient.sendRequest({
    url: `subjects/${id}`,
    method: 'GET',
  });
  return response.data;
}

export async function createSubject(payload: Partial<Omit<Subject, 'id'>>): Promise<Subject> {
  const response = await httpClient.sendRequest({
    url: `subjects`,
    method: 'POST',
    data: payload,
  });
  return response.data;
}

export async function updateSubject(payload: Partial<Subject>): Promise<Subject> {
  const response = await httpClient.sendRequest({
    url: `subjects`,
    method: 'PUT',
    data: payload,
  });
  return response.data;
}

export interface ImportResponse {
  success: string[];
  errors: Array<{
    index: number;
    message: string;
  }>;
}

export async function createBulkSubject(payload: Subject[]): Promise<ImportResponse> {
  const response = await httpClient.sendRequest({
    url: 'subjects/bulk',
    method: 'POST',
    data: payload,
  });
  return response.data;
}
